import { useState, useEffect, useRef } from "react";
import { ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useMutation } from '@apollo/client';
import { ADD_TO_CART_SUBMIT, DELETE_CART_SUBMIT, GET_MY_CART_ITEMS } from '../Graphql/cartQueries';
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { getMediaOwner } from "@/data/media-owners";
import * as am5 from "@amcharts/amcharts5";
import * as am5hierarchy from "@amcharts/amcharts5/hierarchy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import ReactFC from "react-fusioncharts";
import FusionCharts from "fusioncharts";
import Column2D from "fusioncharts/fusioncharts.charts";
import Pie2D from "fusioncharts/fusioncharts.charts";
import FusionTheme from "fusioncharts/themes/fusioncharts.theme.fusion";
import moment from "moment";
import { generateMockData } from "@/Graphql/MockData";
import { CART_PAGE_ITEM, PROPERTY_DETAILS, PROPERTY_DETAILS_CHART_QUERY, GALLERY_IMAGE_ASSETS_QUERY } from "@/Graphql/Queries";
import client, { MAPBOX_TOKEN } from "@/Graphql/apolloClient";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useCart } from "@/utils/cartContext";
import mapboxgl from "mapbox-gl";
import { isValid } from "date-fns";


interface CampaignBillboardDetailProps {
  base_path: string;
  currencyCode: string;
}

// Set Mapbox access token
mapboxgl.accessToken = MAPBOX_TOKEN;

// Map Component
function BillboardMap({ latitude, longitude, title }: { latitude?: number; longitude?: number; title: string }) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Use provided coordinates or default to a general location
    const lat = latitude || -25.2744; // Default to Australia center
    const lng = longitude || 133.7751;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [lng, lat],
      zoom: latitude && longitude ? 15 : 5, // Zoom in if we have exact coordinates
    });

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl());

    // Add marker if we have coordinates
    if (latitude && longitude) {
      new mapboxgl.Marker({
        color: '#10b981' // Emerald color to match theme
      })
        .setLngLat([lng, lat])
        .setPopup(
          new mapboxgl.Popup({ offset: 25 })
            .setHTML(`<div style="padding: 8px;"><strong>${title}</strong></div>`)
        )
        .addTo(map.current);
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [latitude, longitude, title]);

  return <div ref={mapContainer} className="w-full h-full rounded-lg" />;
}

// Initialize FusionCharts
ReactFC.fcRoot(FusionCharts, Column2D, Pie2D, FusionTheme);

// Set FusionCharts license key
const licenseKey = import.meta.env.VITE_APP_FUSIONCHARTS_LICENSE_KEY;
if (licenseKey) {
  (FusionCharts as any).options.license({
    key: licenseKey,
    creditLabel: false
  });
}

// AmCharts Hierarchy Component
function AmChartsHierarchy({ audienceData }: { audienceData: any }) {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);

    // Use ZoomableContainer for pan/zoom
    const container = root.container.children.push(
      am5.ZoomableContainer.new(root, {
        width: am5.percent(100),
        height: am5.percent(100),
      })
    );

    const series = container.children.push(
      am5hierarchy.ForceDirected.new(root, {
        singleBranchOnly: false,
        downDepth: 1,
        initialDepth: 2,
        nodePadding: 1,
        valueField: "value",
        categoryField: "name",
        childDataField: "children",
        minRadius: 35,
        maxRadius: 80,
        centerStrength: 0.8,
        manyBodyStrength: -15,
      })
    );

    // Configure consistent font sizes for all labels
    series.labels.template.setAll({
      fontSize: 16,
      textAlign: "center",
      fontWeight: "600",
      fill: am5.color("#FFFFFF"),
      oversizedBehavior: "wrap",
      maxWidth: 140,
      fontFamily: "inherit",
      lineHeight: 1.2
    });

    // Add pan/zoom tools
    container.children.push(
      am5.ZoomTools.new(root, { target: container })
    );

    // Transform API response to always show segment names as bubbles
    // Ensure all segments are direct children of 'Audience'
    const transformToHierarchy = (segments: any[]) => {
      if (!Array.isArray(segments)) return { name: "Audience", children: [] };
      return {
        name: "Audience",
        children: segments.map(seg => {
          // If seg has children, use them, else fallback
          if (Array.isArray(seg.children) && seg.children.length > 0) {
            return {
              name: seg.name,
              children: seg.children.map((child: any) => ({
                name: child.name,
                value: child.value || 5
              }))
            };
          } else if (Array.isArray(seg.firstLevel) && seg.firstLevel.length > 0) {
            return {
              name: seg.name,
              children: seg.firstLevel.map((child: any) => ({
                name: child.name,
                value: child.value || 5
              }))
            };
          } else {
            return {
              name: seg.name,
              value: seg.value || 5
            };
          }
        })
      };
    };

    const chartData = Array.isArray(audienceData)
      ? transformToHierarchy(audienceData)
      : audienceData;
    series.data.setAll([chartData]);
    series.set("selectedDataItem", series.dataItems[0]);

    // Ensure consistent font sizes after chart is rendered
    series.events.on("boundschanged", () => {
      series.dataItems.forEach((dataItem) => {
        const label = dataItem.get("label");
        if (label) {
          label.setAll({
            fontSize: 16,
            fontWeight: "600",
            fill: am5.color("#FFFFFF"),
            textAlign: "center",
            oversizedBehavior: "wrap",
            maxWidth: 140,
            fontFamily: "inherit",
            lineHeight: 1.2
          });
        }
      });
    });

    // Additional event to ensure root bubble font consistency
    series.events.on("datavalidated", () => {
      if (series.dataItems[0]) {
        const rootLabel = series.dataItems[0].get("label");
        if (rootLabel) {
          rootLabel.setAll({
            fontSize: 16,
            fontWeight: "600",
            fill: am5.color("#FFFFFF"),
            textAlign: "center",
            oversizedBehavior: "wrap",
            maxWidth: 140,
            fontFamily: "inherit",
            lineHeight: 1.2
          });
        }
      }
    });

    series.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, [audienceData]);

  // Match InventoryDetails.tsx chart style and center chart visually
  return (
    <div
      ref={chartRef}
      style={{
        width: "100%",
        height: "100%",
        margin: "0 auto",
        background: "#f8fafc",
        borderRadius: "8px",
        border: "1px solid #e3e3e3",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        position: "relative"
      }}
    />
  );
}

// Audience Analysis Bubble Chart Component
function AudienceChart() {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);

    const series = root.container.children.push(
      am5hierarchy.ForceDirected.new(root, {
        singleBranchOnly: false,
        downDepth: 2,
        topDepth: 1,
        initialDepth: 2,
        valueField: "value",
        categoryField: "name",
        childDataField: "children",
        idField: "name",
        linkWithField: "linkWith",
        manyBodyStrength: -10,
        centerStrength: 0.8
      })
    );

    const data = {
      name: "AUDIENCE",
      children: [
        {
          name: "DEMOGRAPHICS",
          value: 100,
          children: [
            { name: "Age 25-44", value: 45 },
            { name: "Professionals", value: 38 },
            { name: "Urban Dwellers", value: 32 }
          ]
        },
        {
          name: "INTERESTS",
          value: 80,
          children: [
            { name: "Technology", value: 25 },
            { name: "Finance", value: 22 },
            { name: "Travel", value: 20 }
          ]
        },
        {
          name: "ACTIONS",
          value: 60,
          children: [
            { name: "Shopping", value: 20 },
            { name: "Commuting", value: 25 },
            { name: "Entertainment", value: 18 }
          ]
        }
      ]
    };

    series.data.setAll([data]);
    series.set("selectedDataItem", series.dataItems[0]);

    // Make stuff animate on load
    series.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, []);

  return <div ref={chartRef} style={{ width: "100%", height: "400px" }}></div>;
}

const getUnitDisplay = (unit: string | undefined) => {
  if (unit === 'Meter') return 'm';
  if (unit === 'Feet') return 'ft';
  return '';
};

export default function CampaignBillboardDetail({ base_path, currencyCode }: CampaignBillboardDetailProps) {
  const navigate = useNavigate();
  // Helper to format date as 'yyyy-MM-dd'
  function formatDate(date: string | Date) {
    const d = typeof date === 'string' ? new Date(date) : date;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  const [addtoCartQuery] = useMutation(ADD_TO_CART_SUBMIT);
  const [deleteCartQuery] = useMutation(DELETE_CART_SUBMIT);
  const location = useLocation();
  const token = localStorage.getItem("authToken");
  const userId = localStorage.getItem("userId");
  const companyId = import.meta.env.VITE_APP_COMPANY_ID;
  let currentPriceFromState = location.state?.currentPrice;
  const currencyCodeFromState = location.state?.currencyCode || currencyCode;
  const priceFormatFromState = location.state?.priceFormat;
  let startDate = location.state?.startDate;
  let endDate = location.state?.endDate;
  let campaignNameFromState = location.state?.campaignName;
  let from = location.state?.from;
  let packageIdFromState = location.state?.packageId || null;


  const params = useParams();
  const { toast } = useToast();
  const { addToCart, removeFromCart, isInCart: checkIsInCart, priceFormat, setCartItemCount, cartCountryId, hasCartCountry } = useCart();
  const [activeTab, setActiveTab] = useState("location");
  const [selectedImage, setSelectedImage] = useState(0);
  const [cartData, setCartData] = useState<any[]>([]);

  // State for billboard data
  const [billboard, setBillboard] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isClassicType, setClassicType] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billboardAssets, setBillboardAssets] = useState<any>({});

  // Get environment variables and configuration
  const countryId = localStorage.getItem('countryId');

  // Check URL parameters to determine where to navigate back
  const urlParams = new URLSearchParams(window.location.search);
  const fromStore = urlParams.get('from') === 'store';
  const ownerId = urlParams.get('ownerId');
  // Get billboard type from URL parameter, default to 'digital' if not provided
  const billboardType = urlParams.get('type') || 'digital';

  const [config, setConfig] = useState<any[]>([]);
  const [audiencetab, setAudiencetab] = useState<string>('yes');
  const [configurationTab, setConfigurationTab] = useState<string>('yes');

  const [audienceData, setAudienceData] = useState<any[]>([]);
  const [selectedAudience, setSelectedAudience] = useState<string>("Top 5");
  const [loadingAudienceChart, setLoadingAudienceChart] = useState(false);
  const [errorAudienceChart, setErrorAudienceChart] = useState<string | null>(null);


  const cartInitializationRef = useRef(false);
  const initialCartLoadDone = useRef(false);

  const [campaignId, setCampaignId] = useState<string | null>(() => {
    const id = urlParams.get('draftId');
    return id ? id : " ";
  });
  const [edit, setEdit] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('edit') === 'true';
  });
  const [campaignName, setCampaignName] = useState<string>(() => {
    const date = new Date();
    return `Campaign_${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}_${Math.random().toString(36).substring(7)}`;
  });
  const [source, setSource] = useState('');

  // Check if price is 0 or N/A
  const price = currentPriceFromState || billboard?.price?.amount || billboard?.price?.price || 0;
  const isPriceZeroOrNA = !price || price === 0 || price === 'N/A';

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    const campaignId = urlParams.get('draftId');
    const campaignNameParam = urlParams.get('campaignName');
    const editFlag = urlParams.get('edit') === 'true';
    const sourceParam = urlParams.get('source') || '';
    if (editFlag && campaignId && campaignNameParam) {
      setCampaignId(campaignId);
      setCampaignName(campaignNameParam);
      setEdit(editFlag); // Set edit mode to true for draft campaigns
      setSource(sourceParam);
    }
  })


  useEffect(() => {
    const fetchConfigData = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_APP_FOLDER_PATH}/json/configJson.json?t=${new Date().getTime()}`);
        const configData = await response.json();
        const propertypageconfig = configData?.find((page: { name: string; }) => page.name === "Inventory Details");
        if (propertypageconfig) {
          const configdata = propertypageconfig?.configurations || [];
          const audienceSection = propertypageconfig.configurations.find((config: any) => config.key === "audiences");
          if (audienceSection) {
            const audienceTabDefaultValue = audienceSection.fields.find((field: any) => field.key === "Audience Tab")?.default || 'yes';
            setAudiencetab(audienceTabDefaultValue);
          }
          const configurationSection = propertypageconfig.configurations.find((config: any) => config.key === "configurations");
          if (configurationSection) {
            const configurationTabDefaultValue = configurationSection.fields.find((field: any) => field.key === "Configuration Tab")?.default || 'yes';
            setConfigurationTab(configurationTabDefaultValue);
          }
          setConfig(configdata);
        }
      } catch (error) {
        console.error("Error fetching configuration:", error);
      }
    };
    fetchConfigData();
  }, []);


  const isFieldEnabled = (key: string, config: any[]) => {
    const fieldConfig = config.flatMap(cfg => cfg.fields || []).find(field => field.key === key);
    return fieldConfig ? fieldConfig.default : true;
  };

  const isAudienceTabDisabled = !isFieldEnabled('demographic segment', config) && !isFieldEnabled('audience segment', config);

  // Fetch billboard assets for gallery
  const fetchBillboardAssets = async (billboardData: any) => {
    if (!billboardData?.id) return;

    try {
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        const mockAssets = generateMockData(GALLERY_IMAGE_ASSETS_QUERY);
        setBillboardAssets(mockAssets);
      } else {
        const { data } = await client.query({
          query: GALLERY_IMAGE_ASSETS_QUERY,
          variables: {
            sort: "last_modified_date,desc",
            metadataBillboardId: billboardData.id,
            metadataType: billboardData.type || billboardData.inventoryType || billboardData.format || "",
          },
          fetchPolicy: 'network-only'
        });
        setBillboardAssets({ [`_${billboardData.id}`]: data.galleryImageAssets || [] });
      }
    } catch (err) {
      console.error('Error fetching billboard assets:', err);
      // Fallback to default images
      setBillboardAssets({
        [`_${billboardData.id}`]: [
          { filePath: billboardData.thumbnailPath || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg` }
        ]
      });
    }
  };

  // Get all billboard assets for gallery
  const getBillboardAssets = (id?: string): string[] => {
    const billboardId = id || billboard?.id;
    if (!billboardId) {
      return [`${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`];
    }
    const assets = billboardAssets[`_${billboardId}`];
    if (!assets || assets.length === 0) {
      return [`${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`];
    }
    return assets.map((asset: any) => asset.filePath);
  };

  // Fetch billboard details using GraphQL
  const fetchBillboardDetails = async () => {
    setIsLoading(true);
    setError(null);
    try {
      let billboardData;
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        const mockResponse = await generateMockData(PROPERTY_DETAILS);
        billboardData = mockResponse.data.propertyDetails;
      } else {
        const { data } = await client.query({
          query: PROPERTY_DETAILS,
          variables: {
            id: params?.id,
            countryId: cartCountryId || countryId,
            billboardType: billboardType,
            startDate: moment().format("YYYY-MM-DD"),
            endDate: moment().endOf('month').format("YYYY-MM-DD"),
          },
          fetchPolicy: 'network-only',
        });
        billboardData = data.propertyDetails;
      }
      setBillboard(billboardData);
      setClassicType(billboardData.type?.toLowerCase() === 'classic');
      console.log("mapped data-->", billboardData);

      // Fetch billboard assets after setting billboard data
      await fetchBillboardAssets(billboardData);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch billboard details');
      setBillboard(null);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAudienceSegmentChart = async () => {
    console.log("audience segmant function called....");

    setLoadingAudienceChart(true);
    setErrorAudienceChart(null);
    try {
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        const mockResponse = await generateMockData(PROPERTY_DETAILS_CHART_QUERY);
        const fetchedData = mockResponse.data;
        if (
          fetchedData &&
          fetchedData.bubbleChartData &&
          fetchedData.bubbleChartData[0] &&
          fetchedData.bubbleChartData[0].segments
        ) {
          setAudienceData(fetchedData.bubbleChartData[0].segments);
        } else {
          setAudienceData([]);
          setErrorAudienceChart("No audience segment data available.");
        }
      } else {
        const response = await client.query({
          query: PROPERTY_DETAILS_CHART_QUERY,
          variables: {
            accessToken: token,
            id: params?.id,
            countryId: countryId,
            rankLimit: selectedAudience,
            companyId: '',
            exposure: import.meta.env.VITE_APP_EXPOSURE
          },
        });
        const fetchedData = response.data;
        if (
          fetchedData &&
          fetchedData.bubbleChartData &&
          fetchedData.bubbleChartData[0] &&
          fetchedData.bubbleChartData[0].segments
        ) {
          setAudienceData(fetchedData.bubbleChartData[0].segments);
        } else {
          setAudienceData([]);
          setErrorAudienceChart("No audience segment data available.");
        }
      }
    } catch (error) {
      setErrorAudienceChart("Error fetching audience segment data.");
      setAudienceData([]);
    } finally {
      setLoadingAudienceChart(false);
    }
  };


  // Effect to fetch billboard details when component mounts or ID changes
  useEffect(() => {
    if (params?.id) {
      fetchBillboardDetails();
    }
  }, [params?.id, billboardType]);

  // Effect to fetch audience segment chart when selectedAudience changes
  useEffect(() => {
    if (params?.id) {
      fetchAudienceSegmentChart();
    }
  }, [selectedAudience]);

  // Gallery images array - use billboard assets for full resolution images
  const galleryImages = billboard ? [
    ...getBillboardAssets()
      .filter((assetUrl) => assetUrl && typeof assetUrl === 'string' && assetUrl.trim() !== '' && !assetUrl.includes('nomedia.jpg'))
      .map((assetUrl, index) => ({
        src: assetUrl,
        alt: `Gallery image ${index + 1}`,
        isMain: false
      }))
  ] : [];



  // Check if billboard is in cart
  // Check if billboard is in cart (local context or draft data)
  const actuallyInCart = billboard
    ? checkIsInCart(billboard.id) || (edit && cartData.some(item => item.cartItemId === billboard.id || item.billboardId === billboard.id))
    : false;

  const handleAddToCart = async () => {
    if (!billboard) return;
    console.log("billboard check ");

    let countryString = 'Unknown';
    if (typeof billboard.countryName === 'string') {
      countryString = billboard.countryName;
    } else if (typeof billboard.country === 'string') {
      countryString = billboard.country;
    } else if (billboard.countryName && typeof billboard.countryName === 'object' && billboard.countryName.name) {
      countryString = billboard.countryName.name;
    } else if (billboard.country && typeof billboard.country === 'object' && billboard.country.name) {
      countryString = billboard.country.name;
    }

    const cartItem = {
      accessToken: token,
      userId: userId,
      campaignName: campaignNameFromState || campaignName || '',
      startDate: startDate ? formatDate(startDate) : '',
      endDate: endDate ? formatDate(endDate) : '',
      cartItemName: billboard.name,
      cartItemId: billboard.id,
      cartItemReferenceId: billboard.referenceId,
      cartItemType: billboard.type,
      cartItemCountry: billboard?.country?.name,
      cartItemVenueType: billboard.venueType,
      cartItemResolution: billboard.sizeResolution || billboard.resolution || 'Unknown',
      cartItemThumbnailUrl: billboard.thumbnailPath && billboard.thumbnailPath !== "null"
        ? billboard.thumbnailPath
        : `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
      cartItemLatitude: billboard.latitude,
      cartItemLongitude: billboard.longitude,
      packageDetails: billboard.packageDetails || '',
      isSpotBased: (priceFormat === "spot-based" || priceFormatFromState === "spot-based") ? true : false,
      // state: typeof billboard.state === 'object' ? (billboard.state.name || billboard.state.id || '') : (billboard.state || ''),
    };
    try {
      const { data, errors } = await addtoCartQuery({ variables: { input: [cartItem] } });
      if (data) {
        addToCart({
          id: billboard.id.toString(),
          name: billboard.name,
          location: billboard.location || (typeof billboard.state === 'object' ? (billboard.state.name || billboard.state.id || '') : (billboard.state || '')),
          state: typeof billboard.state === 'object' ? (billboard.state.name || billboard.state.id || '') : (billboard.state || ''),
          price: Number(billboard.price) * 100,
          impressions: billboard.monthlySummary?.totalVisitors || 0,
          uniqueVisitors: billboard.monthlySummary?.uniqueVisitors || 0,
          frequency: billboard.monthlySummary?.frequency || 0,
          size: billboard.sizeResolution,
          type: billboard.type,
          status: billboard.availableBooking ? "available" : "sold-out",
          image: billboard.thumbnailPath || billboard.imageUrl,
          currencyCode: billboard?.price?.localCurrencies?.code || 'N/A',
          specifications: {
            resolution: billboard.sizeResolution,
            duration: billboard.type === "Classic" ? "30 days" : "Variable"
          },
          isSpotBased: (priceFormat === "spot-based" || priceFormatFromState === "spot-based") ? true : false,
          priceFormat: priceFormat
        });
        toast({
          title: "Success",
          description: "Billboard has been added to your cart successfully.",
        });
        fetchCartCountDetails();
      } else if (errors && errors.length > 0) {
        toast({
          title: "Error",
          description: errors[0].message,
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        variant: "destructive"
      });
    }
  };

  const fetchCartCountDetails = async () => {
    try {
      let validStartDate = startDate;
      let validEndDate = endDate;

      // If dates are strings, convert to Date objects
      if (typeof validStartDate === 'string') validStartDate = new Date(validStartDate);
      if (typeof validEndDate === 'string') validEndDate = new Date(validEndDate);

      if (!isValid(validStartDate) || !isValid(validEndDate)) {
        console.error("Invalid dates detected:", { startDate, endDate });
        throw new Error("Invalid date values for cart count fetch.");
      }

      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        try {
          const mockData = await generateMockData(GET_MY_CART_ITEMS);
          // Count the items from cartpageItem array for mock data
          const count = mockData.data?.cartpageItem?.length || 0;
          setCartData(mockData.data?.cartpageItem || []);
          setCartItemCount(count);
          window.dispatchEvent(new Event('cartUpdated'));
          return count;
        } catch (error) {
          console.error("Error generating mock cart data:", error);
          setCartData([]);
          return 0;
        }
      } else {
        try {
          console.log("[EDIT] edit flow check --->", edit, campaignId);
          if (edit) {
            console.log("[EDIT] Fetching cart for edit mode with campaignId:", campaignId);
            const { data: editData } = await client.query({
              query: CART_PAGE_ITEM,
              fetchPolicy: "network-only",
              variables: {
                accessToken: token,
                campaignId: campaignId || '',
                userId: userId,
                edit: edit,
                startDate: formatDate(validStartDate),
                endDate: formatDate(validEndDate),
                exposure: import.meta.env.VITE_APP_EXPOSURE,
              },
            });
            console.log("[EDIT] editData?.cartPageItem -------------->", editData?.cartPageItem);
            const fetchedCartDataList = editData?.cartPageItem || [];
            const totalItems = fetchedCartDataList.reduce((count: number, cart: any) => {
              return count + (cart.campaignInventories?.length || 0);
            }, 0);
            setCartItemCount(totalItems);
            // For draft mode, set the campaignInventories array for handleRemoveFromCart to work properly
            setCartData(editData?.cartPageItem[0]?.campaignInventories || []);
            window.dispatchEvent(new Event('cartUpdated'));
            return totalItems;
          } else {
            const { data } = await client.query({
              query: GET_MY_CART_ITEMS, // Query for fetching cart items
              fetchPolicy: "network-only",
              variables: {
                accessToken: token,
                userId: userId,
              }
            });

            // Count the items from cartpageItem array
            const count = data?.cartpageItem?.length || 0;
            setCartData(data?.cartpageItem || []);
            setCartItemCount(count);
            window.dispatchEvent(new Event('cartUpdated'));
            return count;
          }
        } catch (error) {
          console.error("Error fetching cart count:", error);
          setCartData([]);
          toast({
            title: "Error",
            description: "Failed to fetch cart count.",
            variant: "destructive"
          });
          return 0;
        }
      }
    } catch (error) {
      console.error("Error fetching cart count:", error);
      setCartData([]);
      toast({
        title: "Error",
        description: "Failed to fetch cart count.",
        variant: "destructive"
      });
      return 0;
    }
  };

  useEffect(() => {
    // Fetch cart count details when component mounts
    fetchCartCountDetails();
  }, [])

  // Using a ref to track if a request is in progress
  const cartRequestInProgress = useRef(false);
  const [isCartLoading, setIsCartLoading] = useState(false);


  const fetchCartDetails = async () => {
    // If a request is already in progress, skip this one
    if (cartRequestInProgress.current) {
      // Skip duplicate cart fetch requests
      return;
    }

    cartRequestInProgress.current = true;
    setIsCartLoading(true);

    try {
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        const mockdata = await generateMockData(CART_PAGE_ITEM);
        const campaignData = mockdata.data.campaign || {};
        setCartData(mockdata.data.cartPageItem || []);
        // setCartDataList(mockdata.data.cartPageItem || []);

      } else {
        // Don't mark existing cart items as loading when fetching cart details
        // We only want to track new items being added as loading

        // Ensure startDate and endDate are not null since they're non-nullable in the GraphQL schema
        if (!startDate || !endDate) {
          console.error('Start date or end date is missing for cart fetch');
          toast({
            title: "Error",
            description: "Campaign dates are required to fetch cart details",
            variant: "destructive"
          });
          cartRequestInProgress.current = false;
          setIsCartLoading(false);
          return;
        }
        const { data } = await client.query({
          query: CART_PAGE_ITEM,
          fetchPolicy: "network-only",
          variables: {
            accessToken: token,
            campaignId: campaignId || '',
            userId: userId,
            edit: edit,
            startDate: formatDate(startDate),
            endDate: formatDate(endDate),
            exposure: import.meta.env.VITE_APP_EXPOSURE,
          },
        });

        const fetchedCartDataList = data.cartPageItem || [];
        console.log("fetchedCartDataList check", fetchedCartDataList[0]?.campaignInventories?.length);


        // If no cart data is returned, ensure cart count is 0
        if (fetchedCartDataList[0]?.campaignInventories?.length === 0) {
          console.log('No cart data found, setting cart count to 0');
          // setCartDataList([]);
          setCartData([]);
          return;
        }

        // Create a Set of billboard IDs that are in the cart
        const cartBillboardIds = new Set(
          fetchedCartDataList.flatMap((cart: any) =>
            cart.campaignInventories?.map((inv: any) => inv.billboardId)
          ).filter(Boolean)
        );

        // Update billboard data with cart status - Important for keeping consistent cart state
        // setBillboard((prevData: any) =>
        //   prevData.map((billboard: any) => ({
        //     ...billboard,
        //     isInCart: cartBillboardIds.has(billboard.id),
        //   }))
        // );

        // Update filtered billboards with cart status
        // setFilteredBillboards((prev: any[]) =>
        //   prev.map((billboard) => ({
        //     ...billboard,
        //     isInCart: cartBillboardIds.has(billboard.id),
        //   }))
        // );

        // Add state information to each billboard in the cart
        // const enhancedCartData = fetchedCartDataList.map((cartItem: any) => {
        //   if (cartItem.campaignInventories && cartItem.campaignInventories.length > 0) {
        //     return {
        //       ...cartItem,
        //       campaignInventories: cartItem.campaignInventories.map((inventory: any) => {
        //         // Find the corresponding billboard to get its state
        //         const matchingBillboard = billboard.find((bb: any) => bb.id === inventory.billboardId);
        //         return {
        //           ...inventory,
        //           billboardState: matchingBillboard?.stateName || '',
        //         };
        //       }),
        //     };
        //   }
        //   return cartItem;
        // });

        // setCartDataList(enhancedCartData);
        // setCartData(enhancedCartData);

        // Update cart count based on the number of items in the cart
        const totalItems = fetchedCartDataList.reduce((count: number, cart: any) => {
          return count + (cart.campaignInventories?.length || 0);
        }, 0);
        console.log(`Setting cart count to ${totalItems} based on fetched cart data`);
        // ADD THIS MISSING LINE:
        setCartItemCount(totalItems);

        // Notify other components
        window.dispatchEvent(new Event('cartUpdated'));
      }
    } catch (error) {
      console.error("Error fetching cart details:", error);
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        console.log("Using mock data, ignoring API error");
      } else {
        // Provide more specific error messages based on the error type

        if (error instanceof Error) {
          if (error.message.includes('startDate') || error.message.includes('endDate')) {
            toast({
              title: "Error",
              description: "Campaign dates are required to fetch cart details",
              variant: "destructive"
            });
          } else {
            toast({
              title: "Error",
              description: `Failed to fetch cart details: ${error.message}`,
              variant: "destructive"
            });
          }
        } else {
          toast({
            title: "Error",
            description: "Failed to fetch cart details. Please try again.",
            variant: "destructive"
          });
        }
      }
    } finally {
      setIsCartLoading(false);
      cartRequestInProgress.current = false;
      // We intentionally don't reset loadingCartItemIds here
      // as that would mark all cart items as "loading"
    }
  };

  // Add useEffect to initialize cart state on component mount with better safeguards
  useEffect(() => {
    // Check if the component just mounted or if dates changed
    if (!initialCartLoadDone.current) {

      // Fetch cart details with valid dates
      fetchCartDetails();

      // Mark initial cart load as done
      initialCartLoadDone.current = true;
      cartInitializationRef.current = true;
    }
  }, []); // Removed startDate and endDate dependencies to prevent refetching when dates change


  const handleRemoveFromCart = async () => {
    if (!billboard) return;

    // Find the cart item to be deleted from our fetched cartData.
    // This single find-logic works for both draft (edit) and normal cart flows:
    // - In "draft" mode, it matches using `item.billboardId === billboard.id`
    // - In "normal" mode, it matches using `item.cartItemId === billboard.id`
    const cartItem = cartData.find(
      item => (item.cartItemId === billboard.id) || (item.billboardId === billboard.id)
    );

    if (!cartItem) {
      toast({
        title: "Error",
        description: "Billboard not found in the current cart.",
        variant: "destructive"
      });
      return;
    }

    // Determine the correct ID to send to the mutation based on the flow.
    // - In "edit" (draft) mode, the ID to delete is in `cartItem.cartItemId`.
    // - In "normal" mode, the ID to delete is in `cartItem.id`.
    const deleteId = edit ? cartItem.cartItemId : cartItem.id;
    const itemType = edit ? "draft-data" : "cart-data";

    if (!deleteId) {
      toast({
        title: "Error",
        description: "Could not determine item ID for deletion.",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await client.mutate({
        mutation: DELETE_CART_SUBMIT,
        variables: {
          accessToken: token,
          deleteItems: [{
            id: deleteId,
            itemType: itemType,
          }]
        },
        fetchPolicy: "no-cache",
      });

      if (response?.data?.deleteCartItem?.success) {
        removeFromCart(billboard.id.toString());
        // Refetch cart details to update state and count
        fetchCartCountDetails();
        toast({
          title: "Success",
          description: response.data.deleteCartItem.message || "Billboard has been removed from your cart.",
        });
      } else {
        const errorMessage = response.errors?.[0]?.message || response.data?.deleteCartItem?.message || "Failed to delete item";
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove item from cart",
        variant: "destructive"
      });
    }
  };
  const handleBackClick = () => {
    console.log("location.state check handle back click--->", location?.state);
    console.log("Draft details:", campaignName, campaignId, edit, source);

    const from = location?.state?.from;
    const packageIdFromState = location?.state?.packageId;
    const billboardType = location?.state?.type || 'digital';
    const priceFormatFromState = location?.state?.priceFormat;
    const campaignNameFromState = location?.state?.campaignName;

    // Base path for property list
    let backUrl = `${base_path}/propertylist`;

    // --- 1️⃣ If coming from a package page ---
    if (from === 'package' && packageIdFromState) {
      console.log("Navigating back to package details page...");
      const packageUrl = `/package/${packageIdFromState}?type=${encodeURIComponent(billboardType)}`;

      // Include draft params if in edit flow
      const draftParams = edit
        ? `&draftId=${campaignId}&campaignName=${encodeURIComponent(campaignName)}&edit=true&source=${encodeURIComponent(source || '')}`
        : '';

      navigate(`${packageUrl}${draftParams}`, {
        state: {
          startDate,
          endDate,
          campaignName: campaignNameFromState || campaignName,
          currentPrice: location.state?.currentPrice || 0,
          from: 'package',
        },
      });

      return; // stop further execution after navigating
    }

    // --- 2️⃣ If coming from landing page ---
    if (
      priceFormatFromState &&
      (priceFormatFromState === 'landing-page' || priceFormatFromState === 'landing-page-million')
    ) {
      console.log("Navigating back to landing page...");
      navigate(`${base_path}/`);
      return;
    }

    // --- 3️⃣ Default: navigate back to property list ---
    localStorage.setItem('clearStartCampaignFilters', 'true');

    // Pass current country ID in URL (like Content Hub pattern)
    const currentCountryId = localStorage.getItem('selectedCountryId');
    
    // Add draft params for edit flow
    if (edit && campaignId && campaignName) {
      backUrl += `?draftId=${campaignId}&campaignName=${encodeURIComponent(campaignName)}&edit=true&source=${encodeURIComponent(source || '')}`;
      if (currentCountryId) {
        backUrl += `&countryId=${currentCountryId}`;
      }
    } else if (currentCountryId) {
      // Non-draft: just append countryId
      backUrl += `?countryId=${currentCountryId}`;
    }

    console.log("Navigating back to property list:", backUrl);
    navigate(backUrl);
  };


  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <div className="h-32 bg-gray-300 rounded mb-4"></div>
              <div className="h-6 bg-gray-300 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-300 rounded w-1/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!billboard) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Billboard Not Found</h2>
            <p className="text-gray-600">The requested billboard could not be found.</p>
          </div>
        </div>
      </div>
    );
  }
  function formatTime12Hour(time: string | number): string {
    if (typeof time === "number") {
      const hours = Math.floor(time / 3600);
      const minutes = Math.floor((time % 3600) / 60);
      return formatTime12Hour(`${hours}:${minutes.toString().padStart(2, "0")}`);
    }
    if (typeof time === "string") {
      const [hourStr, minuteStr = "00"] = time.split(":");
      let hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      hour = hour % 12 || 12;
      return `${hour}:${minute.toString().padStart(2, "0")} ${ampm}`;
    }
    return "N/A";
  }

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="max-w-[95vw] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <div className="flex items-center justify-end mb-3">
          <Button
            variant="outline"
            onClick={() => handleBackClick()}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        {/* Header Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              {/* Billboard Image */}
              <div className="relative w-full lg:w-64 h-48 lg:h-32">
                <img
                  src={billboard.thumbnailPath && billboard.thumbnailPath !== "null"
                    ? billboard.thumbnailPath
                    : `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`}
                  alt={billboard.name}
                  className="w-full h-full object-cover rounded-lg"
                />
                <div className="absolute top-2 left-2">
                  <Badge className={
                    isPriceZeroOrNA
                      ? "bg-red-100 text-red-800"
                      : billboard.availableBooking
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                  }>
                    {isPriceZeroOrNA
                      ? "Unavailable"
                      : billboard.availableBooking
                        ? "Available"
                        : "Sold Out"}
                  </Badge>
                </div>
              </div>

              {/* Basic Info */}
              <div className="flex-1">
                <div>
                  <h1
                    className="text-2xl font-bold text-gray-900 break-words leading-tight"
                    title={billboard.displayName || billboard.name}
                  >
                    {billboard.displayName || billboard.name}
                  </h1>
                </div>

                {/* State and District */}
                <p className="text-gray-600 text-sm mb-1">{billboard.referenceId}</p>
                <div className="flex items-center gap-1 text-sm mb-3">
                  {isFieldEnabled('district', config) && (
                    <p className="text-emerald-600">
                      {billboard.district.name}
                    </p>
                  )}
                  {isFieldEnabled('state', config) && isFieldEnabled('district', config) && (
                    <span className="text-emerald-600">,</span>
                  )}
                  {isFieldEnabled('state', config) && (
                    <p className="text-emerald-600">{billboard.state.name}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 block">Size (W x H)</span>
                    <span className="font-medium">
                      {(() => {
                        const unit = getUnitDisplay(billboard.specification?.unit);
                        return [1, 2, 3, 4, 5, 6].map((num) => {
                          let width = (billboard?.specification as Record<string, any>)[`panel${num}Width`];
                          let height = (billboard?.specification as Record<string, any>)[`panel${num}Height`];
                          if ((!width || !height) && num === 1) {
                            width = (billboard?.specification as Record<string, any>)["panelWidth"];
                            height = (billboard?.specification as Record<string, any>)["panelHeight"];
                          }
                          return width && height ? (
                            <div key={`panel${num}`}>{`${width} ${unit} X ${height} ${unit}`}</div>
                          ) : null;
                        }).filter(Boolean).length > 0 ? (
                          [1, 2, 3, 4, 5, 6].map((num) => {
                            let width = (billboard?.specification as Record<string, any>)[`panel${num}Width`];
                            let height = (billboard?.specification as Record<string, any>)[`panel${num}Height`];
                            if ((!width || !height) && num === 1) {
                              width = (billboard?.specification as Record<string, any>)["panelWidth"];
                              height = (billboard?.specification as Record<string, any>)["panelHeight"];
                            }
                            return width && height ? (
                              <div key={`panel${num}`}>{`${width} ${unit} X ${height} ${unit}`}</div>
                            ) : null;
                          }).filter(Boolean)
                        ) : (
                          'N/A'
                        );
                      })()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 block mb-1">
                      {billboard?.type?.toLowerCase() === 'classic' ? 'Number Of Faces' : 'Number Of Screens'}
                    </span>
                    <span className="font-medium">
                      {billboard?.type?.toLowerCase() === 'classic'
                        ? (billboard.specification?.faces ?? 'N/A')
                        : (billboard.specification?.screens ?? 'N/A')}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">Impressions</span>
                    <span className="font-medium">{(priceFormat === "spot-based" || priceFormatFromState === "spot-based") ? billboard?.impressionPerSpot?.toLocaleString() || '0' : (priceFormat === "day-based" || priceFormatFromState === "day-based") ? billboard?.impressionPerDay?.toLocaleString() || '0' : billboard?.monthlySummary?.totalVisitors?.toLocaleString() || 'N/A'}</span>
                  </div>
                  {!billboard.availableBooking && (
                    <div>
                      <span className="text-gray-500 block">Next Available Date</span>
                      <span className="font-medium">{billboard.nextAvailableDate ? moment(billboard.nextAvailableDate).format("DD MMM YYYY") : 'N/A'}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Price Section - aligned to right */}
              <div className="flex flex-col items-end gap-4">
                <div className="text-2xl font-bold text-emerald-600">
                  {billboard.price?.localCurrencies?.code || currencyCodeFromState}{" "}
                  {currentPriceFromState ? currentPriceFromState?.toLocaleString() : "0"}
                  {priceFormatFromState === 'landing-page-million'
                    ? "/Day"
                    : priceFormatFromState === 'landing-page'
                      ? "/Month"
                      : (billboard.type === 'DIGITAL'
                        ? (priceFormat === "spot-based" || priceFormatFromState === "spot-based"
                          ? " /Spot"
                          : " /Day")
                      : "/Month")}
              </div>
              
              {/* Add to Cart Button - below price */}
              <div className="flex gap-2">
                {from !== 'package' && (
                (() => {
                  const price = currentPriceFromState || billboard.price?.amount || billboard.price?.price || 0;
                  const isPriceZeroOrNA = !price || price === 0 || price === 'N/A';
                  const isDisabled = isPriceZeroOrNA || !billboard.availableBooking;
                  
                  return (
                    <Button
                      onClick={actuallyInCart ? handleRemoveFromCart : handleAddToCart}
                      disabled={isDisabled}
                      className={`min-w-[120px] ${
                        actuallyInCart
                          ? "bg-red-600 hover:bg-red-700 text-white"
                          : isDisabled
                          ? "bg-gray-400 cursor-not-allowed text-white"
                          : "bg-emerald-600 hover:bg-emerald-700 text-white"
                      }`}
                    >
                      {isPriceZeroOrNA
                        ? 'Unavailable'
                        : !billboard.availableBooking
                        ? 'Sold Out'
                        : actuallyInCart
                        ? "Remove from Cart"
                        : 'Add to Cart'}
                    </Button>
                  );
                })()
                )}
              </div>
            </div>
            
              {/* Media Owner Visit Store Button */}
              {billboard.mediaOwner && (() => {
                const mediaOwner = getMediaOwner(billboard.mediaOwner);
                if (mediaOwner) {
                  return (
                    <Link to={`/store/${mediaOwner.id}`}>
                      <Button variant="outline" className="w-full mt-2">
                        Visit {mediaOwner.name} Store
                      </Button>
                    </Link>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>

        {/* Tabbed Content */}
        <div className="bg-white rounded-xl shadow-sm">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-5 bg-gray-50 p-1 rounded-t-xl">
              <TabsTrigger value="location" className="text-sm">Location</TabsTrigger>
              <TabsTrigger value="specification" className="text-sm">Specifications</TabsTrigger>

              {audiencetab === 'yes' && (
                <TabsTrigger value="audiences" className="text-sm">Audiences</TabsTrigger>
              )}
              {isFieldEnabled('asset', config) && (
                <TabsTrigger value="gallery" className="text-sm">Gallery</TabsTrigger>
              )}
              {configurationTab === "yes" && billboard.type?.toLowerCase() === "digital" && (
                <TabsTrigger value="configuration" className="text-sm">Configurations</TabsTrigger>
              )}
            </TabsList>

            <div className="p-6">
              <TabsContent value="location" className="space-y-6 mt-0">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Map Area */}
                  <div className="lg:col-span-2">
                    <div className="h-64 lg:h-80 rounded-lg overflow-hidden">
                      <BillboardMap
                        latitude={billboard.latitude}
                        longitude={billboard.longitude}
                        title={billboard.displayName || billboard.name}
                      />
                    </div>
                  </div>

                  {/* Location Details */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {isFieldEnabled('site owner', config) && (
                        <div>
                          <span className="text-gray-500 block mb-1">Media Owner</span>
                          {billboard.mediaOwnerName && (() => {
                            const mediaOwner = getMediaOwner(billboard.memediaOwnerNamediaOwner);
                            if (mediaOwner) {
                              return (
                                <div className="flex items-center space-x-2">
                                  <img
                                    src={mediaOwner.logo}
                                    alt={mediaOwner.name}
                                    className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                                  />
                                  <span className="font-medium text-emerald-600 hover:text-emerald-700 cursor-pointer">{mediaOwner.name}</span>
                                  {/* <Link to={`/store/${mediaOwner.id}`}>
                                  <span className="font-medium text-emerald-600 hover:text-emerald-700 cursor-pointer">{mediaOwner.name}</span>
                                </Link> */}
                                </div>
                              );
                            }
                            return <span className="font-medium">{billboard.mediaOwnerName}</span>;
                          })() || <span className="font-medium">Not specified</span>}
                        </div>
                      )}
                      {isFieldEnabled('billboard type', config) && (
                        <div>
                          <span className="text-gray-500 block mb-1">Type</span>
                          <span className="font-medium">
                            {billboard.type?.toLowerCase() === 'digital' ? (
                              <>
                                Digital Inventory
                              </>
                            ) : billboard.type?.toLowerCase() === 'classic' ? (
                              <>
                                Classic Inventory
                              </>
                            ) : (
                              <>
                                N/A
                              </>
                            )}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500 block mb-1">Latitude</span>
                        <span className="font-medium">{billboard.latitude || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 block mb-1">Longitude</span>
                        <span className="font-medium">{billboard.longitude || 'N/A'}</span>
                      </div>
                      {isFieldEnabled('site address', config) && (
                        <div>
                          <span className="text-gray-500 block mb-1">Address</span>
                          <span className="font-medium">{billboard.formattedAddress || 'N/A'}</span>
                        </div>
                      )}
                      {isFieldEnabled('board facing', config) && (
                        <div>
                          <span className="text-gray-500 block mb-1">Board Facing</span>
                          <span className="font-medium">{billboard.facingDirection || 'N/A'}</span>
                        </div>
                      )}
                      {isFieldEnabled('category', config) && (
                        <div>
                          <span className="text-gray-500 block mb-1">Category</span>
                          <span className="font-medium">{billboard.category || 'N/A'}</span>
                        </div>
                      )}
                      {/* <div>
                        <span className="text-gray-500 block mb-1">Site Address</span>
                        <span className="font-medium">{typeof billboard.state === 'object' ? billboard.state.name || billboard.state.id || 'N/A' : billboard.state || 'N/A'}</span>
                      </div> */}
                      {isFieldEnabled('location type', config) && (
                        <div className="col-span-2">
                          <span className="text-gray-500 block mb-1">Venue type</span>
                          <span className="font-medium">{billboard.venueType}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="specification" className="space-y-6 mt-0">
                <div className="space-y-6">
                  {/* Video Specification */}
                  {billboard?.type?.toLowerCase() === 'digital' && (
                    isFieldEnabled('video support', config) ||
                    isFieldEnabled('image support', config) ||
                    isFieldEnabled('feature support', config) ||
                    isFieldEnabled('audio support', config) ||
                    isFieldEnabled('panel format', config)
                  ) ? (
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Video specification</h3>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        {isFieldEnabled('video support', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Video support</span>
                            <span className="font-medium">
                              {Array.isArray(billboard.specification?.videoSupport)
                                ? billboard.specification.videoSupport.join(', ')
                                : billboard.specification?.videoSupport || 'N/A'}
                            </span>
                          </div>
                        )}
                        {isFieldEnabled('image support', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Image support</span>
                            <span className="font-medium">
                              {Array.isArray(billboard.specification?.imageSupport)
                                ? billboard.specification.imageSupport.join(', ')
                                : billboard.specification?.imageSupport || 'N/A'}
                            </span>
                          </div>
                        )}
                        {isFieldEnabled('feature support', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Feature Support</span>
                            <span className="font-medium">
                              {Array.isArray(billboard.specification?.featureSupport)
                                ? billboard.specification.featureSupport.join(', ')
                                : billboard.specification?.featureSupport || 'N/A'}
                            </span>
                          </div>
                        )}
                        {isFieldEnabled('audio support', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Audio Support</span>
                            <span className="font-medium">
                              {Array.isArray(billboard.specification?.audioFormats)
                                ? billboard.specification.audioFormats.join(', ')
                                : billboard.specification?.audioFormats || 'N/A'}
                            </span>
                          </div>
                        )}
                        {isFieldEnabled('panel format', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Board Type</span>
                            <span className="font-medium">{billboard?.format || 'N/A'}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}


                  {/* Board Specification */}
                  {(
                    isFieldEnabled('number of panels', config) ||
                    isFieldEnabled('number of screens', config) ||
                    isFieldEnabled('lead time(Days)', config) ||
                    (isFieldEnabled('PlaySoftware', config) && !isClassicType) ||
                    (isFieldEnabled('resolution', config) && billboard?.type?.toLowerCase() === 'digital') ||
                    isFieldEnabled('size', config) ||
                    isFieldEnabled('language support', config) ||
                    isFieldEnabled('content approval', config) ||
                    (isFieldEnabled('total Sqft', config) && isClassicType) ||
                    isFieldEnabled('exclusion', config) ||
                    isFieldEnabled('sell as network', config)
                  ) ? (
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Board specification</h3>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        {isFieldEnabled('number of panels', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Number of panels</span>
                            <span className="font-medium">{billboard.specification.panel || 'N/A'}</span>
                          </div>
                        )}
                        {isFieldEnabled('number of screens', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">
                              {billboard.type?.toLowerCase() === 'classic' ? 'Number of Faces' : 'Number of screens'}
                            </span>
                            <span className="font-medium">
                              {billboard?.type?.toLowerCase() === 'classic'
                                ? (billboard.specification?.faces ?? 'N/A')
                                : (billboard.specification?.screens ?? 'N/A')}
                            </span>
                          </div>
                        )}
                        {isFieldEnabled('lead time(Days)', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Lead time (Days)</span>
                            <span className="font-medium">{billboard?.specification?.leadTime != null
                              ? Number(billboard?.specification?.leadTime)
                              : 'N/A'}</span>
                          </div>
                        )}
                        {isFieldEnabled('PlaySoftware', config) && !isClassicType && (
                          <div>
                            <span className="text-gray-500 block mb-1">Player Software</span>
                            <span className="font-medium">{billboard.specification?.playerSoftware || billboard.specification?.playerSoftwareModule?.name || 'N/A'}</span>
                          </div>
                        )}
                        {isFieldEnabled('resolution', config) && billboard?.type?.toLowerCase() === 'digital' && (
                          <div>
                            <span className="text-gray-500 block mb-1">Resolution (W x H)</span>
                            <span className="font-medium">
                              {[1, 2, 3, 4, 5, 6].map((num) => {
                                const width = billboard?.specification?.[`resolution${num}Width`];
                                const height = billboard?.specification?.[`resolution${num}Height`];
                                return width && height ? (
                                  <div key={`resolution${num}`}>{`${width} px X ${height} px`}</div>
                                ) : null;
                              }).filter(Boolean).length > 0 ? (
                                [1, 2, 3, 4, 5, 6].map((num) => {
                                  const width = billboard?.specification?.[`resolution${num}Width`];
                                  const height = billboard?.specification?.[`resolution${num}Height`];
                                  return width && height ? (
                                    <div key={`resolution${num}`}>{`${width} px X ${height} px`}</div>
                                  ) : null;
                                }).filter(Boolean)
                              ) : (
                                'N/A'
                              )}
                            </span>
                          </div>
                        )}
                        {isFieldEnabled('size', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Size (W x H)</span>
                            <span className="font-medium">
                              {(() => {
                                const unit = getUnitDisplay(billboard.specification?.unit);
                                return [1, 2, 3, 4, 5, 6].map((num) => {
                                  let width = (billboard?.specification as Record<string, any>)[`panel${num}Width`];
                                  let height = (billboard?.specification as Record<string, any>)[`panel${num}Height`];
                                  if ((!width || !height) && num === 1) {
                                    width = (billboard?.specification as Record<string, any>)["panelWidth"];
                                    height = (billboard?.specification as Record<string, any>)["panelHeight"];
                                  }
                                  return width && height ? (
                                    <div key={`panel${num}`}>{`${width} ${unit} X ${height} ${unit}`}</div>
                                  ) : null;
                                }).filter(Boolean).length > 0 ? (
                                  [1, 2, 3, 4, 5, 6].map((num) => {
                                    let width = (billboard?.specification as Record<string, any>)[`panel${num}Width`];
                                    let height = (billboard?.specification as Record<string, any>)[`panel${num}Height`];
                                    if ((!width || !height) && num === 1) {
                                      width = (billboard?.specification as Record<string, any>)["panelWidth"];
                                      height = (billboard?.specification as Record<string, any>)["panelHeight"];
                                    }
                                    return width && height ? (
                                      <div key={`panel${num}`}>{`${width} ${unit} X ${height} ${unit}`}</div>
                                    ) : null;
                                  }).filter(Boolean)
                                ) : (
                                  'N/A'
                                );
                              })()}
                            </span>
                          </div>
                        )}
                        {isFieldEnabled('language support', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Language support</span>
                            <span className="font-medium">Not Disclosed</span>
                          </div>
                        )}
                        {isFieldEnabled('content approval', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Content approval</span>
                            <span className="font-medium">{billboard.specification.contentApproval ? 'Yes' : 'No'}</span>
                          </div>
                        )}
                        {isFieldEnabled('total Sqft', config) && isClassicType && (
                          <div>
                            <span className="text-gray-500 block mb-1">Total SQFT</span>
                            <span className="font-medium">{Number(billboard.specification.panel1Size) || 'N/A'}</span>
                          </div>
                        )}
                        {isFieldEnabled('exclusion', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Exclusion</span>
                            <span className="font-medium">Not Disclosed</span>
                          </div>
                        )}
                        {isFieldEnabled('sell as network', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Sell As Network</span>
                            <span className="font-medium">{billboard.specification.sellAsNetwork ? 'Yes' : 'No'}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              </TabsContent>

              <TabsContent value="audiences" className="space-y-6 mt-0">
                <div className="space-y-6">
                  {/* Audience Metrics */}
                  <div className="grid grid-cols-3 gap-6 text-center">
                    {isFieldEnabled('impressions', config) && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-2xl font-bold text-gray-900">{billboard?.impressionPerDay?.toLocaleString() || 'N/A'}</div>
                        <div className="text-sm text-gray-500">Impressions</div>
                      </div>
                    )}
                    {isFieldEnabled('reach', config) && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-2xl font-bold text-gray-900">{billboard?.monthlySummary?.uniqueVisitors?.toLocaleString() || 'N/A'}</div>
                        <div className="text-sm text-gray-500">Reach</div>
                      </div>
                    )}
                    {isFieldEnabled('average frequency', config) && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-2xl font-bold text-gray-900">{billboard?.monthlySummary?.frequency?.toFixed(2) || 'N/A'}</div>
                        <div className="text-sm text-gray-500">Average frequency</div>
                      </div>
                    )}
                  </div>

                  {/* Audience Analysis Sub-tabs */}
                  <Tabs defaultValue="demographic" className="w-full">
                    <div className="flex gap-4 mb-6">
                      {(isFieldEnabled('demographic segment', config) || isFieldEnabled('audience segment', config)) && (
                        <TabsList className="bg-blue-50">
                          {isFieldEnabled('demographic segment', config) && (
                            <TabsTrigger value="demographic" className="text-sm">Demographic details</TabsTrigger>
                          )}
                          {isFieldEnabled('audience segment', config) && (
                            <TabsTrigger value="analysis" className="text-sm">Audience analysis</TabsTrigger>
                          )}
                        </TabsList>
                      )}
                      {/* Only show dropdown in Audience Analysis tab */}
                      {activeTab === "audiences" && (
                        <TabsContent value="analysis" asChild>
                          <div className="ml-auto">
                            <select
                              className="text-sm border rounded px-3 py-1 bg-white"
                              value={selectedAudience}
                              onChange={e => {
                                setSelectedAudience(e.target.value);
                              }}
                            >
                              <option value="All">All</option>
                              <option value="Top 10">Top 10</option>
                              <option value="Top 5">Top 5</option>
                            </select>
                          </div>
                        </TabsContent>
                      )}
                    </div>
                    {isFieldEnabled('demographic segment', config) && (
                      <TabsContent value="demographic" className="mt-0">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                          {/* Age Demographics Bar Chart - FusionCharts */}
                          <div>
                            <h3 className="text-lg font-semibold mb-4">Age (in %)</h3>
                            <ReactFC
                              type="column2d"
                              width="100%"
                              height="350"
                              dataFormat="JSON"
                              dataSource={{
                                chart: {
                                  caption: "",
                                  xAxisName: "Age Groups",
                                  yAxisName: "Percentage",
                                  numberSuffix: "%",
                                  theme: "fusion",
                                  showValues: "1",
                                  paletteColors: "#2563eb",
                                  bgColor: "#FFFFFF",
                                  borderAlpha: "0",
                                  canvasBorderAlpha: "0",
                                  usePlotGradientColor: "0",
                                  plotBorderAlpha: "0",
                                  showAlternateHGridColor: "0",
                                  showToolTip: "1",
                                  plotSpacePercent: "40",
                                  valueFontSize: "14",
                                  labelFontSize: "12",
                                  baseFontSize: "12",
                                  plotFillAlpha: "90",
                                  showHoverEffect: "1"
                                },
                                data: [
                                  {
                                    label: "Senior",
                                    value: billboard?.monthlySummary?.seniorPercentage || 0,
                                  },
                                  {
                                    label: "Adult",
                                    value: billboard?.monthlySummary?.adultPercentage || 0,
                                  },
                                  {
                                    label: "Young Adult",
                                    value: billboard?.monthlySummary?.youngAdultPercentage || 0
                                  }
                                ]
                              }}
                            />
                          </div>

                          {/* Gender Pie Chart - FusionCharts */}
                          <div>
                            <h3 className="text-lg font-semibold mb-4">Gender</h3>
                            <ReactFC
                              type="pie2d"
                              width="100%"
                              height="350"
                              dataFormat="JSON"
                              dataSource={{
                                chart: {
                                  caption: "",
                                  showPercentValues: "1",
                                  showValues: "1",
                                  theme: "fusion",
                                  paletteColors: "#2563eb,#94a3b8",
                                  bgColor: "#FFFFFF",
                                  borderAlpha: "0",
                                  use3DLighting: "0",
                                  showShadow: "0",
                                  enableSmartLabels: "1",
                                  startingAngle: "310",
                                  showLabels: "1",
                                  showLegend: "1",
                                  legendPosition: "bottom",
                                  legendBorderAlpha: "0",
                                  legendShadow: "0",
                                  legendItemFontSize: "12",
                                  legendItemFontColor: "#374151",
                                  pieRadius: "120",
                                  valueFontSize: "14",
                                  labelFontSize: "12",
                                  baseFontSize: "12",
                                  showHoverEffect: "1",
                                  pieSliceDepth: "15",
                                  manageLabelOverflow: "1"
                                },
                                data: [
                                  { label: "Male", value: billboard?.monthlySummary?.malePercentage || 0, color: "#2563eb" },
                                  { label: "Female", value: billboard?.monthlySummary?.femalePercentage || 0, color: "#94a3b8" }
                                ]
                              }}
                            />
                          </div>
                        </div>
                      </TabsContent>
                    )}

                    <TabsContent value="analysis" className="mt-0">
                      {/* Audience Analysis - amCharts Hierarchy Chart */}
                      <div className="h-[600px]">
                        <AmChartsHierarchy audienceData={audienceData} />
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </TabsContent>

              <TabsContent value="gallery" className="space-y-6 mt-0">
                <div className="space-y-6">
                  {/* Main Image Display */}
                  <div className="rounded-lg overflow-hidden bg-gray-100">
                    {galleryImages[selectedImage] ? (
                      <img
                        src={galleryImages[selectedImage].src}
                        alt={galleryImages[selectedImage].alt}
                        className="w-full h-64 lg:h-96 object-contain"
                      />
                    ) : (
                      <img
                        src={`${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`}
                        alt="No image available"
                        className="w-full h-64 lg:h-96 object-contain"
                      />
                    )}
                  </div>

                  {/* Thumbnail Grid */}
                  <div className="grid grid-cols-6 gap-2">
                    {galleryImages
                      .filter(image => image.src && typeof image.src === 'string' && image.src.trim() !== '' && !image.src.includes('nomedia.jpg'))
                      .map((image, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImage(index)}
                          className={`relative rounded-lg overflow-hidden bg-gray-100 aspect-video ${selectedImage === index ? 'ring-2 ring-emerald-500' : ''}`}
                        >
                          <img
                            src={image.src}
                            alt={image.alt}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="configuration" className="space-y-6 mt-0">
                <div className="space-y-6">
                  {/* Spot Configuration */}
                  {(isFieldEnabled('loop duration', config) ||
                    isFieldEnabled('spots/hour', config) ||
                    isFieldEnabled('max. Spot/Day', config) ||
                    isFieldEnabled('min. Spot/Day', config)) && (
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Spot configuration</h3>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                          {isFieldEnabled('loop duration', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1">Loop Duration</span>
                              <span className="font-medium">{billboard.price.loopDuration || 'N/A'} Seconds</span>
                            </div>
                          )}
                          {isFieldEnabled('spots/hour', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1">Spot duration</span>
                              <span className="font-medium">{billboard.price.spotDuration || 'N/A'} Seconds</span>
                            </div>
                          )}
                          {isFieldEnabled('max. Spot/Day', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1">Max. Spots Per Day</span>
                              <span className="font-medium">{billboard.price.maximumSpotDurationPerDay || 0}</span>
                            </div>
                          )}
                          {isFieldEnabled('min. Spot/Day', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1">Min. Spots Per Day</span>
                              <span className="font-medium">{billboard.price.minimumSpotDurationPerDay || 0}</span>
                            </div>
                          )}
                          {/* <div>
                        <span className="text-gray-500 block mb-1">Availability</span>
                        <span className="font-medium">{billboard.nextAvailableDate ? moment(billboard.nextAvailableDate).format("DD MMM YYYY") : 'N/A'}</span>
                      </div> */}
                        </div>
                      </div>
                    )}

                  {/* Board Configuration */}
                  {(isFieldEnabled('operations hours', config) ||
                    isFieldEnabled('no.of Clients per Hour', config) ||
                    isFieldEnabled('mode of operation', config) ||
                    isFieldEnabled('programmatic enabled', config) ||
                    isFieldEnabled('timezone', config)) && (
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Board configuration</h3>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                          {isFieldEnabled('operations hours', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1">Operation hours</span>
                              <span className="font-medium">
                                {billboard.price.displayOnTime && billboard.price.displayOffTime
                                  ? `${formatTime12Hour(billboard.price.displayOnTime)} - ${formatTime12Hour(billboard.price.displayOffTime)}`
                                  : "N/A"}
                              </span>
                            </div>
                          )}
                          {isFieldEnabled('no.of Clients per Hour', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1">Spots For Sale</span>
                              <span className="font-medium">{billboard.price.contentFrequencyPerHour || 1}</span>
                            </div>
                          )}
                          {isFieldEnabled('mode of operation', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1">Mode Of Operation</span>
                              <span className="font-medium">{billboard.price.modeOfOperation || 'Loop Based'}</span>
                            </div>
                          )}
                          {isFieldEnabled('programmatic enabled', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1">Programmatic Enabled</span>
                              <span className="font-medium">{billboard.price.programmaticEnabled ? 'Yes' : 'No'}</span>
                            </div>
                          )}
                          {isFieldEnabled('no.of Clients per Hour', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1"> Max advertisers</span>
                              <span className="font-medium">{billboard.price.clients || 'N/A'}</span>
                            </div>
                          )}
                          {isFieldEnabled('timezone', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1">Timezone</span>
                              <span className="font-medium">{billboard.timezone || 'N/A'}</span>
                            </div>
                          )}
                          {/* <div>
                        <span className="text-gray-500 block mb-1">Brightness</span>
                        <span className="font-medium">Auto adjust</span>
                      </div>
                      <div>
                        <span className="text-gray-500 block mb-1">Weather resistance</span>
                        <span className="font-medium">IP65</span>
                      </div>
                      <div>
                        <span className="text-gray-500 block mb-1">Connectivity</
                      </div> */}
                        </div>
                      </div>
                    )}

                  {/* Operation Information */}
                  {(isFieldEnabled('available days', config) || isFieldEnabled('near by POI', config)) && (
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Operation information</h3>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        {isFieldEnabled('near by POI', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Nearby POIs</span>
                            <span className="font-medium">{billboard.price.nearbyPOIs || 'Not Disclosed'}</span>
                          </div>
                        )}
                        {isFieldEnabled('available days', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Available</span>
                            <span className="font-medium">
                              {Array.isArray(billboard?.price?.availableDays)
                                ? billboard.price.availableDays.join(', ')
                                : typeof billboard?.price?.availableDays === 'string' && billboard.price.availableDays !== ''
                                  ? billboard.price.availableDays
                                  : 'N/A'}
                            </span>
                          </div>
                        )}
                        {/* <div>
                          <span className="text-gray-500 block mb-1">Installation date</span>
                          <span className="font-medium">Jan 2023</span>
                        </div>
                        <div>
                          <span className="text-gray-500 block mb-1">Last maintenance</span>
                          <span className="font-medium">Nov 2024</span>
                        </div>
                        <div>
                          <span className="text-gray-500 block mb-1">Uptime</span>
                          <span className="font-medium">99.8%</span>
                        </div>
                        <div>
                          <span className="text-gray-500 block mb-1">Remote monitoring</span>
                          <span className="font-medium">Active</span>
                        </div> */}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
